import "./CalendarPassQuestionScreen.css";
import nextIcon from "../../../assets/images/score-card-next-icon.svg";
import appIcon from "../../../assets/images/header-sabehub-app-icon.svg";
import backIcon from "../../../assets/images/calendar-pass-question-back-icon.svg";
import emptyStar from "../../../assets/images/score-card/score-card-empty-star.svg";
import filledStar from "../../../assets/images/score-card/score-card-filled-star.svg";
import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { highlightLastWord } from "utils/utils";
import { useMutation } from "@tanstack/react-query";
import useWSMApi from "services/api/useWSMApi";
import ROUTE_PATHS from "utils/routePaths";

interface SlotPassQuestion {
  id: string;
  stage: number;
  step: number;
  slot_pass_type: string;
  question: string;
  options: string[] | null;
  description?: string | null;
  placeholder?: string | null;
  is_multi_select: boolean;
}

const CalendarPassQuestionScreen = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { updateReservation } = useWSMApi();
  const questions: SlotPassQuestion[] = location.state?.questions ?? [];
  const stage: string = location.state?.stage ?? "";
  const slotId: string = location.state?.slot_id ?? "";
  const slotPassType: string | undefined = location.state?.slot_pass_type;
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string[]>>({});
  // comment text per question id
  const [comments, setComments] = useState<Record<string, string>>({});
  const currentQuestion = questions[currentQuestionIndex] ?? null;
  const placeholder = currentQuestion?.placeholder ?? "";
  const isLastQuestion = currentQuestionIndex === questions.length - 1;
  const hasOptions =
    currentQuestion?.options != null && currentQuestion.options.length > 0;
  const showTextarea = !hasOptions;
  const selectedOptions: string[] = currentQuestion
    ? (answers[currentQuestion.id] ?? [])
    : [];
  const commentText: string = currentQuestion
    ? (comments[currentQuestion.id] ?? "")
    : "";

  // Disabled when:
  // - question has options but none are selected, OR
  // - question has no options (textarea) but comment is empty
  const isNextDisabled = (() => {
    if (!currentQuestion) return true;
    if (hasOptions) return selectedOptions.length === 0;
    return commentText.trim().length === 0;
  })();

  const updateReservationMutation = useMutation({
    mutationFn: (data: any) => updateReservation(data),
    onSuccess: () => {
      if (stage === "4") {
        navigate(ROUTE_PATHS.WSM_CALENDAR_PASS_LAST_STAGE_THANK_YOU_SCREEN);
      } else {
        navigate(ROUTE_PATHS.WSM_CALENDAR_PASS_THANK_YOU_SCREEN);
      }
    },
  });

  const handleOptionSelect = (option: string) => {
    if (!currentQuestion) return;

    if (currentQuestion.is_multi_select) {
      setAnswers((prev) => {
        const existing = prev[currentQuestion.id] ?? [];
        const updated = existing.includes(option)
          ? existing.filter((o) => o !== option)
          : [...existing, option];
        return { ...prev, [currentQuestion.id]: updated };
      });
    } else {
      setAnswers((prev) => ({
        ...prev,
        [currentQuestion.id]: [option],
      }));
    }
  };

  const handleCommentChange = (text: string) => {
    if (!currentQuestion) return;
    setComments((prev) => ({ ...prev, [currentQuestion.id]: text }));
  };

  const handleNext = () => {
    if (isNextDisabled || !currentQuestion) return;

    // Capture the final answers state including the current question
    const finalAnswers = { ...answers };
    const finalComments = { ...comments };

    if (!isLastQuestion) {
      setCurrentQuestionIndex((prev) => prev + 1);
      return;
    }

    // Build selected options: one entry per question in order
    const selectedOptionsPayload: string[][] = questions.map((q) => {
      const opts = finalAnswers[q.id] ?? [];
      const comment = finalComments[q.id]?.trim();
      if (comment) {
        return [...opts, comment];
      }
      return opts;
    });

    const stageKey =
      stage === "4"
        ? "fourth_stage_selected_options"
        : "first_stage_selected_options";

    updateReservationMutation.mutate({
      slot_id: slotId,
      ...(slotPassType ? { slot_pass_type: slotPassType } : {}),
      [stageKey]: selectedOptionsPayload,
    });
  };

  const handleBack = () => {
    if (currentQuestionIndex === 0) {
      navigate(-1);
      return;
    }
    setCurrentQuestionIndex((prev) => prev - 1);
  };

  if (!currentQuestion) return null;

  return (
    <div className="wms-mobile-container">
      <div className="calendar-pass-question-screen-container">
        <div className="calendar-pass-question-screen-header-sticky">
          <header className="calendar-pass-question-screen-header-bar">
            <img
              src={appIcon}
              alt="app-logo"
              className="calendar-pass-question-screen-header-app-icon"
            />
            <div className="calendar-pass-question-screen-header-divider"></div>
            <span>{highlightLastWord("Intention Setting")}</span>
          </header>
        </div>

        <div className="calendar-pass-question-screen-content-container">
          {currentQuestion.description && (
            <div className="screen-title-container">
              {currentQuestion.description}
            </div>
          )}

          <div className="question-container">
            <span>{currentQuestionIndex + 1}.</span>
            <span>{highlightLastWord(currentQuestion.question, 4)}</span>
          </div>

          {/* Options — only rendered when the question has options */}
          {hasOptions && (
            <div className="options-container">
              {currentQuestion.options!.map((option) => {
                const isSelected = selectedOptions.includes(option);
                return (
                  <div
                    key={option}
                    className={`option-card ${isSelected ? "selected" : ""}`}
                    onClick={() => handleOptionSelect(option)}
                  >
                    <img
                      src={isSelected ? filledStar : emptyStar}
                      alt="selection-icon"
                    />
                    {/* <div
                      className={`option-indicator ${currentQuestion.is_multi_select ? "checkbox" : "radio"
                        } ${isSelected ? "checked" : ""}`}
                    /> */}
                    {option}
                  </div>
                );
              })}
            </div>
          )}

          {/* Comment textarea — shown when options is null or empty */}
          {showTextarea && (
            <div className="comment-container">
              <textarea
                className="comment-textarea"
                placeholder={placeholder}
                value={commentText}
                onChange={(e) => handleCommentChange(e.target.value)}
                rows={4}
              />
            </div>
          )}

          {/* Next / Done button */}
          <div className="button-parent-container">
            <div className="back-button" onClick={handleBack}>
              <img src={backIcon} alt="back-arrow" className="button-image" />
            </div>
            <div
              className={`continue-button ${isNextDisabled ? "disabled" : ""}`}
              onClick={isNextDisabled ? undefined : handleNext}
            >
              <span>{isLastQuestion ? "Done" : "Next"}</span>
              <img src={nextIcon} alt="arrow" className="button-image" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CalendarPassQuestionScreen;
